import Sprite from './ui/Sprite.js';

class ImageStore {
    constructor() {
        this.store = {};
    }

    /**
     * Get an image by path. Returns the image directly if loaded,
     * or loads it lazily (returning a promise) if not yet loaded.
     * @param {string} path - The image path relative to /assets/ (e.g., 'img/my_image.png')
     * @returns {Image|Promise<Image>} The image or a promise that resolves to the image
     */
    get(path) {
        if (this.store[path]) {
            return this.store[path];
        } else {
            // console.warn(`Resource not loaded yet. Loading lazily: ${path}`);

            // Start loading the resource and store a promise for it
            const loadingPromise = this.loadSingleImage(path).catch((error) => {
                console.error(`Failed to load resource ${path}:`, error);
                throw error;
            });

            this.store[path] = loadingPromise; // Temporarily store the promise
            return loadingPromise; // Return the promise so callers can await it if needed
        }
    }

    async loadSingleImage(path, maxRetries = 3, retryDelayMs = 200) {
        try {
            const img = await this.loadImageWithRetry(path, maxRetries, retryDelayMs);
            this.store[path] = img; // Cache the loaded image using the original path as key
            return img;
        } catch (error) {
            console.warn(`Failed to load image ${path}: ${error.message}`);
            throw error; // Re-throw for caller to handle
        }
    }

    async fetchImagePaths() {
        try {
            const response = await fetch('assets/imagePaths.json');
            if (!response.ok) {
                throw new Error(`Failed to fetch image paths: ${response.statusText}`);
            }
            const data = await response.json();
            return data.png_files;
        } catch (error) {
            console.error('Error fetching image paths:', error);
            throw error;
        }
    }

    async loadImageWithRetry(path, maxRetries = 3, retryDelayMs = 200) {
        // Prepend assets/ to the path
        const fullPath = `assets/${path}`;

        let attempts = 0;
        const attemptLoad = () =>
            new Promise((resolve, reject) => {
                const img = new Image();

                img.onload = () => resolve(img);
                img.onerror = () => {
                    attempts++;
                    if (attempts < maxRetries) {
                        console.warn(
                            `Failed to load image: ${fullPath}. ` +
                                `Retrying attempt #${attempts + 1} in ${retryDelayMs}ms...`
                        );
                        setTimeout(() => {
                            attemptLoad().then(resolve).catch(reject);
                        }, retryDelayMs);
                    } else {
                        reject(new Error(`Failed to load image after ${maxRetries} attempts: ${fullPath}`));
                    }
                };
                img.src = fullPath;
            });

        return attemptLoad();
    }

    async loadAllImages(maxRetries = 3, retryDelayMs = 1000) {
        const imagePaths = await this.fetchImagePaths();
        const total = imagePaths.length;

        const results = await Promise.allSettled(
            imagePaths.map((path) => {
                // Load each image - this will store it in the cache
                return this.loadSingleImage(path, maxRetries, retryDelayMs);
            })
        );

        let failedCount = 0;
        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                // Image is already stored in cache by loadSingleImage
            } else {
                failedCount++;
                console.error(`Failed to load ${imagePaths[index]}: ${result.reason?.message}`);
            }
        });

        const summary = `Image loading complete.\nFailed: ${failedCount}\nSucceeded: ${total - failedCount}`;
        console.log(summary);

        return this.store;
    }
}

export const imageStore = new ImageStore();

// SpriteStore can now use synchronous access since images are preloaded
class SpriteStore {
    constructor() {
        this.store = {};
    }

    async loadEssentialSprites() {
        console.info('Loading essential sprites...');

        // These will work synchronously if loadAllImages() was called first
        // Otherwise they return promises (backward compatible)
        const battleBackgroundFoe = new Sprite({
            position: { x: -240, y: 0 },
            image: imageStore.get('img/battle_scene_grass_foe.png'),
            verticalCrop: 16,
        });

        const battleBackgroundPlayer = new Sprite({
            position: { x: 240, y: 0 },
            image: imageStore.get('img/battle_scene_grass_player.png'),
        });

        const battleBackgroundNoStage = new Sprite({
            position: { x: 0, y: 0 },
            image: imageStore.get('img/battle_scene_grass_no_stage.png'),
        });

        const playerBack = new Sprite({
            position: { x: 240 + 32, y: 80 - 16 },
            image: imageStore.get('img/player_back.png'),
            moving: false,
            frames: { val: 1, max: 4 },
        });

        const playerBackGirl = new Sprite({
            position: { x: 240 + 32, y: 80 - 16 },
            image: imageStore.get('img/player/girl/player_back.png'),
            moving: false,
            frames: { val: 1, max: 4 },
        });

        const playerBackBoy = new Sprite({
            position: { x: 240 + 32, y: 80 - 16 },
            image: imageStore.get('img/player/boy/player_back.png'),
            moving: false,
            frames: { val: 1, max: 4 },
        });

        const battleTextBox = new Sprite({
            position: { x: 0, y: 112 },
            image: imageStore.get('img/battle_text_box.png'),
        });

        const battleOptionsBox = new Sprite({
            position: { x: 120, y: 112 },
            frames: { max: 4, val: 0 },
            moving: false,
            image: imageStore.get('img/battle_options.png'),
        });

        const moveOptionsBoxes = new Sprite({
            position: { x: 0, y: 112 },
            moving: false,
            image: imageStore.get('img/move_boxes_with_arrow.png'),
            frames: { val: 0, max: 4 },
        });

        const battleSceneFoeInfo = new Sprite({
            position: { x: -120, y: 0 },
            image: imageStore.get('img/battle_scene_foe_info_no_lv.png'),
        });

        const battleSceneFoeInfoNoHPLabel = new Sprite({
            position: { x: -120, y: 0 },
            image: imageStore.get('img/battle_scene_foe_info_no_lv_no_hp.png'),
        });

        const battleScenePlayerInfo = new Sprite({
            position: { x: 240, y: 96 - 23 },
            image: imageStore.get('img/battle_scene_player_info.png'),
        });

        const statIncreaseBox = new Sprite({
            position: { x: 160, y: 48 + 8 },
            image: imageStore.get('img/stat_increase_box.png'),
            moving: false,
        });

        const continueReadingArrow = new Sprite({
            image: imageStore.get('img/continue_reading_arrow.png'),
            frames: { max: 8, val: 0 },
            hold: 4,
            moving: true,
            visible: false,
        });

        const typeBadge = new Sprite({
            position: { x: 160, y: 48 + 8 },
            image: imageStore.get('img/type_badge.png'),
            moving: false,
            frames: { max: 20, val: 0 },
        });

        const statusBadge = new Sprite({
            image: imageStore.get('img/status_badge.png'),
            moving: false,
            frames: { max: 8, val: 0 },
            position: { x: 0, y: 0 },
            visible: true,
        });

        const pokemonMenuBackground = new Sprite({
            image: imageStore.get('img/pokemon_menu_base.png'),
            moving: false,
            position: { x: 0, y: 0 },
            visible: false,
        });

        const pokemonMenuPrimaryBox = new Sprite({
            image: imageStore.get('img/pokemon_menu_primary_box.png'),
            moving: false,
            position: { x: 0, y: 0 },
            visible: false,
            frames: { val: 0, max: 2 },
        });

        const pokemonMenuSecondaryBox = new Sprite({
            image: imageStore.get('img/pokemon_menu_secondary_box.png'),
            moving: false,
            position: { x: 0, y: 0 },
            visible: false,
            frames: { val: 0, max: 2 },
        });

        const pokemonIcon = new Sprite({
            image: imageStore.get('img/pokemon/mini/0.png'),
            moving: false,
            position: { x: 0, y: 0 },
            visible: false,
        });

        const bagMenuBackgroundSprite = new Sprite({
            position: { x: 0, y: 0 },
            image: imageStore.get('img/bag_menu.png'),
            visible: true,
        });

        const pokedexMenuBackgroundSprite = new Sprite({
            position: { x: 0, y: 0 },
            image: imageStore.get('img/pokedex_background_v2.png'),
            visible: true,
        });

        const leftArrowSprite = new Sprite({
            position: { x: 0, y: 64 },
            image: imageStore.get('img/bag_menu_left_arrow.png'),
            visible: true,
            moving: true,
            frames: { max: 14, val: 0 },
        });

        const rightArrowSprite = new Sprite({
            position: { x: 64, y: 64 },
            image: imageStore.get('img/bag_menu_right_arrow.png'),
            visible: true,
            moving: true,
            frames: { max: 14, val: 0 },
        });

        const bagIconSprite = new Sprite({
            position: { x: 8, y: 32 },
            image: imageStore.get('img/bag_icon.png'),
            visible: true,
            moving: false,
            frames: { max: 3, val: 0 },
        });

        const selectedItemArrowSprite = new Sprite({
            position: { x: 0, y: 0 },
            image: imageStore.get('img/selected_item_arrow.png'),
            visible: true,
        });

        const pokeMartMenuBackgroundSprite = new Sprite({
            position: { x: 0, y: 0 },
            image: imageStore.get('img/poke_mart_menu.png'),
            visible: true,
        });

        const selectedItemIconSprite = new Sprite({
            position: { x: 4, y: 120 },
            image: imageStore.get('img/items/potion.png'),
            visible: true,
        });

        const addedToPokedexSummaryBackgroundSprite = new Sprite({
            image: imageStore.get('img/added_to_pokedex_summary.png'),
            moving: false,
            position: { x: 0, y: 0 },
            frames: { val: 0, max: 1 },
            visible: true,
        });

        const exclamationMark = new Sprite({
            position: { x: 0, y: 0, z: 8 },
            image: imageStore.get('img/npc/exclamation_mark.png'),
            frames: { max: 24 },
            hold: 1,
            moving: false,
            visible: true,
        });

        const foeTrainerFront = new Sprite({
            position: {
                x: 144,
                y: 8,
            },
            image: imageStore.get('img/npc/youngster/front.png'),
            moving: false,
        });

        const missingNoSprite = new Sprite({
            position: { x: 0, y: 0 },
            image: imageStore.get('img/missingno.png'),
            visible: true,
        });

        const frontSpriteSheet = new Sprite({
            position: { x: 0, y: 0 },
            image: imageStore.get('img/spritesheets/pokemon_front_master_spritesheet_20_20.png'),
            isFromSpriteSheet: true,
            spriteSheetCols: 20,
            spriteSheetRows: 20,
            spriteIndex: 0,
            visible: false,
            origin: { x: 0, y: 1 },
        });

        const backSpriteSheet = new Sprite({
            position: { x: 0, y: 0 },
            image: imageStore.get('img/spritesheets/pokemon_back_master_spritesheet_20_20.png'),
            isFromSpriteSheet: true,
            spriteSheetCols: 20,
            spriteSheetRows: 20,
            spriteIndex: 0,
            visible: false,
            origin: { x: 0, y: 1 },
        });

        const miniSpriteSheet = new Sprite({
            position: { x: 0, y: 0 },
            image: imageStore.get('img/spritesheets/pokemon_mini_master_spritesheet_20_20.png'),
            isFromSpriteSheet: true,
            spriteSheetCols: 20,
            spriteSheetRows: 20,
            spriteIndex: 0,
            visible: false,
        });

        this.store = {
            frontSpriteSheet,
            backSpriteSheet,
            miniSpriteSheet,
            battleBackgroundFoe,
            battleBackgroundPlayer,
            battleBackgroundNoStage,
            playerBack,
            battleTextBox,
            battleOptionsBox,
            moveOptionsBoxes,
            battleSceneFoeInfo,
            battleSceneFoeInfoNoHPLabel,
            battleScenePlayerInfo,
            statIncreaseBox,
            continueReadingArrow,
            typeBadge,
            statusBadge,
            pokemonMenuBackground,
            pokemonMenuPrimaryBox,
            pokemonMenuSecondaryBox,
            pokemonIcon,
            bagMenuBackgroundSprite,
            leftArrowSprite,
            rightArrowSprite,
            bagIconSprite,
            selectedItemArrowSprite,
            pokeMartMenuBackgroundSprite,
            selectedItemIconSprite,
            addedToPokedexSummaryBackgroundSprite,
            exclamationMark,
            foeTrainerFront,
            pokedexMenuBackgroundSprite,
            missingNoSprite,
            playerBackBoy,
            playerBackGirl,
        };
        return this.store;
    }
}

export const spriteStore = new SpriteStore();
